import type { AppData, AnyRecord, OrderRow, ProductRow, SkuRow, WorkItem } from "./types";

export function productRows(data?: AppData): ProductRow[] {
  const inventory = data?.inventory;
  const rows = (inventory?.rows || []) as SkuRow[];
  const sourceProducts = new Map<string, AnyRecord>((inventory?.products || []).map((product: AnyRecord) => [
    String(product.productCode || product.productKey || ""),
    product
  ]));
  const grouped = new Map<string, SkuRow[]>();
  for (const row of rows) {
    const key = String(row.productKey || row.productCode || row.customProductCode || row.productName || row.sku || "");
    if (!key) continue;
    grouped.set(key, [...(grouped.get(key) || []), row]);
  }
  return [...grouped.entries()].map(([productKey, group]) => {
    const first = group[0] || {};
    const source = sourceProducts.get(productKey) || {};
    const channelListings = group.flatMap((row) => Array.isArray(row.channelListings) ? row.channelListings : []);
    const mapped = channelListings.filter((listing) => listing.active !== false);
    const images = [source.imageUrl, first.productImageUrl, first.imageUrl].filter(Boolean);
    return {
      productKey,
      productName: String(source.productName || first.productName || productKey),
      seasonCode: String(source.seasonCode || first.seasonCode || first.issuedSeasonCode || "-"),
      categoryName: String(source.categoryCode4 || source.categoryCode || first.categoryName || first.productCategoryCode || "-"),
      skuCount: group.length,
      colorCount: new Set(group.map((row) => row.colorwayName || row.colorwayCode).filter(Boolean)).size,
      sizeCount: new Set(group.map((row) => row.sizeName || row.sizeCode).filter(Boolean)).size,
      onHandTotal: group.reduce((sum, row) => sum + Number(row.onHandTotal || 0), 0),
      allocatedTotal: group.reduce((sum, row) => sum + Number(row.allocatedTotal || 0), 0),
      channelStatus: mapped.length ? `Cafe24 ${mapped.length} SKU` : "매핑 필요",
      imageStatus: images.length ? "이미지 있음" : "이미지 필요",
      rows: group,
      source
    };
  }).sort((a, b) => a.productName.localeCompare(b.productName, "ko"));
}

export function inventoryRows(data?: AppData): SkuRow[] {
  return [...((data?.inventory?.rows || []) as SkuRow[])].sort((a, b) => {
    return String(a.productName || "").localeCompare(String(b.productName || ""), "ko") ||
      String(a.colorwayName || "").localeCompare(String(b.colorwayName || ""), "ko") ||
      String(a.sizeName || "").localeCompare(String(b.sizeName || ""), "ko");
  });
}

export function orderRows(data?: AppData): OrderRow[] {
  const snapshots = data?.snapshots as AnyRecord | undefined;
  const ordersSnapshot = snapshots?.orders as AnyRecord | undefined;
  const payload = ordersSnapshot?.payload as AnyRecord | undefined;
  return Array.isArray(payload?.orders) ? payload.orders as OrderRow[] : [];
}

export function lastOrderPullAt(data?: AppData): string {
  const snapshots = data?.snapshots as AnyRecord | undefined;
  const ordersSnapshot = snapshots?.orders as AnyRecord | undefined;
  const payload = ordersSnapshot?.payload as AnyRecord | undefined;
  return String(payload?.lastPulledAt || ordersSnapshot?.updatedAt || "");
}

export function fulfillmentItems(data?: AppData): AnyRecord[] {
  return Array.isArray(data?.fulfillmentQueue?.items) ? data.fulfillmentQueue.items as AnyRecord[] : [];
}

export function auditLogs(data?: AppData): AnyRecord[] {
  const logs = data?.auditLogs as AnyRecord | undefined;
  return Array.isArray(logs?.logs) ? logs.logs : [];
}

export function onlineMismatchCount(data?: AppData): number {
  return inventoryRows(data).filter((row) => {
    const listings = Array.isArray(row.channelListings) ? row.channelListings : [];
    return listings.some((listing: AnyRecord) => {
      if (listing.remoteQuantity === null || listing.remoteQuantity === undefined) return false;
      return Number(listing.remoteQuantity || 0) !== Number(listing.allocatedQuantity || 0);
    });
  }).length;
}

export function homeWorkItems(data?: AppData): WorkItem[] {
  const rows = inventoryRows(data);
  const orders = orderRows(data);
  const fulfillment = data?.fulfillmentQueue?.summary as AnyRecord | undefined;
  const lowStock = rows.filter((row) => Number(row.availableToSell || 0) <= 3).length;
  const productIssues = productRows(data).filter((product) => product.channelStatus === "매핑 필요").length;
  const returnOrders = orders.filter((order) => order.statusBucket === "return_exchange").length;
  return [
    { key: "ship", title: "출고 대기", body: "피킹 가능하거나 재고 확인이 필요한 주문", count: Number(fulfillment?.open || 0), priority: "높음", target: "/shipping", view: "open" },
    { key: "receive", title: "입고 등록", body: "상품 수량을 실제 오프라인 위치에 반영", count: 0, priority: "보통", target: "/receiving" },
    { key: "returns", title: "반품/교환 확인", body: "고객 요청과 재입고 판단 필요", count: returnOrders, priority: "높음", target: "/orders", view: "return_exchange" },
    { key: "low-stock", title: "저재고 SKU", body: "품절 또는 과판매 위험", count: lowStock, priority: "주의", target: "/inventory", view: "low" },
    { key: "products", title: "상품 등록 확인", body: "판매 채널 준비가 가능한 상품", count: productIssues, priority: "보통", target: "/products", view: "needs_review" }
  ];
}

export function homeExceptions(data?: AppData): WorkItem[] {
  const api = data?.onlineMallApis as AnyRecord | undefined;
  const cafe24 = Array.isArray(api?.providers) ? api.providers.find((provider: AnyRecord) => provider.provider === "cafe24") : null;
  const mismatch = onlineMismatchCount(data);
  const orders = orderRows(data);
  const unmapped = inventoryRows(data).filter((row) => !(row.channelListings || []).length).length;
  return [
    { key: "cafe24", title: "Cafe24 연결 확인", body: "온라인 재고나 상품 상태가 다를 수 있음", count: cafe24?.connected ? 0 : 1, priority: "긴급", target: "/integrations", view: "channels" },
    { key: "online-mismatch", title: "온라인몰 수량 차이", body: "온라인몰 할당수량과 내부 기준 차이", count: mismatch, priority: "높음", target: "/inventory", view: "online_mismatch" },
    { key: "orders", title: "주문 이슈", body: "주소, 결제, 품절, 출고 보류 확인", count: orders.filter((order) => ["cancelled", "needs_check"].includes(String(order.statusBucket || ""))).length, priority: "높음", target: "/orders", view: "needs_check" },
    { key: "unmapped", title: "미매핑 온라인몰 SKU", body: "Cafe24 품목 매핑이 없어 반영 불가", count: unmapped, priority: "주의", target: "/products", view: "unmapped" }
  ];
}

export function locations(data?: AppData): AnyRecord[] {
  return Array.isArray(data?.inventory?.locations) ? data.inventory.locations as AnyRecord[] : [];
}
